/**
 * React Query hooks for WS-5 (fieldiq-api#1295): invoices from
 * customer-approved field tickets — the Well Testing hooks (useWtInvoices.ts)
 * carried over, plus mark-paid through Finance's door as Pipeline & Facility
 * does it (usePfInvoices.ts).
 *
 * Same rules as useWellServicing.ts: every key starts ['wellServicing'] and
 * every write invalidates that one prefix (an invoice moves the register, the
 * billable list, the job's invoices and the ticket's invoice line); every read
 * spreads LOAD_FAILURE_QUERY_OPTIONS so a refusal reads as a refusal, never as
 * an empty list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidateWs, unwrapWs, unwrapWsList } from './useWellServicing';
import type { WsBillableGroup, WsInvoice, WsInvoiceGenerate, WsInvoiceRecipient, WsInvoiceRow, WsInvoiceVoided } from '../types/ws-invoices';

type Params = Record<string, string | undefined>;
const clean = (p?: Params) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v) out[k] = v;
  });
  return Object.keys(out).length ? out : undefined;
};

export const wsInvoiceKeys = {
  invoices: (p?: Params) => ['wellServicing', 'invoices', clean(p) ?? {}] as const,
  billable: () => ['wellServicing', 'invoicesBillable'] as const,
  invoice: (id: string) => ['wellServicing', 'invoice', id] as const,
  recipient: (id: string) => ['wellServicing', 'invoiceRecipient', id] as const,
};

function useWsInvoiceWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWs(qc) });
}

export interface WsInvoiceFilters extends Params {
  status?: string;
  customer_id?: string;
  job_id?: string;
}

export function useWsInvoices(filters?: WsInvoiceFilters, enabled = true) {
  return useQuery({
    queryKey: wsInvoiceKeys.invoices(filters),
    queryFn: async () => unwrapWsList<WsInvoiceRow>((await api.wellServicing.invoices.list(clean(filters))).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsBillable(enabled = true) {
  return useQuery({
    queryKey: wsInvoiceKeys.billable(),
    queryFn: async () => unwrapWsList<WsBillableGroup>((await api.wellServicing.invoices.billable()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWsInvoice(id: string | undefined) {
  return useQuery({
    queryKey: wsInvoiceKeys.invoice(id ?? ''),
    queryFn: async () => unwrapWs<WsInvoice>((await api.wellServicing.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Who a send with no `to` would address — the office's write role only (the API gates it as a write). */
export function useWsInvoiceRecipient(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wsInvoiceKeys.recipient(id ?? ''),
    queryFn: async () => unwrapWs<WsInvoiceRecipient>((await api.wellServicing.invoices.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useGenerateWsInvoice = () =>
  useWsInvoiceWrite(async (body: WsInvoiceGenerate) => unwrapWs<WsInvoice>((await api.wellServicing.invoices.generate(body)).data).data);

export const useSendWsInvoice = () =>
  useWsInvoiceWrite(async ({ id, to, cc }: { id: string; to?: string; cc?: string[] }) => {
    const body: { to?: string; cc?: string[] } = {};
    if (to) body.to = to;
    if (cc && cc.length) body.cc = cc;
    return unwrapWs<WsInvoice & { sentToName?: string | null }>((await api.wellServicing.invoices.send(id, body)).data).data;
  });

/**
 * Mark paid goes through Finance's door (PATCH /unified-invoices/:id), so a
 * success moves BOTH lists: every Well Servicing read, and Finance → Invoices'
 * other-verticals list and its band figures. The PATCH answers with the raw
 * row, which nothing here reads; the refetch draws the result.
 */
export function useMarkWsInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.wellServicing.invoices.markPaid(id)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      qc.invalidateQueries({ queryKey: ['invoice-summary-dashboard'] });
      return invalidateWs(qc);
    },
  });
}

/** Resolves to the voided invoice and the ids of the tickets it released (never dropped on the way). */
export const useVoidWsInvoice = () =>
  useWsInvoiceWrite(async ({ id, reason }: { id: string; reason: string }) => {
    const inv = unwrapWs<WsInvoiceVoided>((await api.wellServicing.invoices.void(id, reason)).data).data;
    return { invoice: inv, releasedTicketIds: Array.isArray(inv?.releasedTicketIds) ? inv!.releasedTicketIds : [] };
  });
